// darkModeContext.tsx

import React, { ReactNode, useCallback, useEffect, useState } from "react";
import { DarkModeContext, DarkModeContextType } from "./theme-context";

interface ThemeContextProviderProps {
  children: ReactNode;
}

export const DarkModeProvider = (props: ThemeContextProviderProps) => {
  const { children } = props;

  const getSystemPrefersDark = () => {
    if (typeof window === "undefined" || !window.matchMedia) return false;
    return window.matchMedia("(prefers-color-scheme: dark)").matches;
  };

  const getInitialDarkMode = () => {
    if (typeof window === "undefined") return false;

    const saved = localStorage.getItem("themePreference");
    if (saved === "dark") return true;
    if (saved === "light") return false;

    return getSystemPrefersDark();
  };

  const [darkMode, setDarkMode] = useState<boolean>(getInitialDarkMode);

  const toggleDarkMode = useCallback(() => {
    setDarkMode((prev) => {
      const next = !prev;
      localStorage.setItem("themePreference", next ? "dark" : "light");
      return next;
    });
  }, []);

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;

    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
    const hasExplicitTheme =
      localStorage.getItem("themePreference") === "dark" ||
      localStorage.getItem("themePreference") === "light";

    if (hasExplicitTheme) return;

    const handleThemeChange = (event: MediaQueryListEvent) => {
      setDarkMode(event.matches);
    };

    mediaQuery.addEventListener("change", handleThemeChange);
    return () => mediaQuery.removeEventListener("change", handleThemeChange);
  }, []);

  const darkModeContextValue: DarkModeContextType = {
    darkMode,
    toggleDarkMode,
  };

  return (
    <DarkModeContext.Provider value={darkModeContextValue}>
      {children}
    </DarkModeContext.Provider>
  );
};
